import React, { useState, useEffect, useMemo } from 'react';
// Dynamic imports for XLSX and jsPDF to avoid Safari iOS crash

// Enhanced Types
interface AdminStats {
    status: string;
    kpis: {
        total_users: number;
        total_clients_managed: number;
        active_subscriptions: number;
        mrr: number;
        total_revenue: number;
        conversion_rate: number;
        arpu: number;
    };
    engagement: {
        active_users: number;
        inactive_count: number;
        super_users: number;
        inactive_list: Array<{
            id: string;
            full_name: string;
            email: string;
            phone: string;
            last_synced_at: string | null;
            created_at: string;
        }>;
    };
    alerts: {
        expiring_soon: Array<{
            id: string;
            full_name: string;
            email: string;
            subscription_plan: string;
            active_until: string;
        }>;
        recently_expired: Array<{
            id: string;
            full_name: string;
            email: string;
            subscription_plan: string;
            active_until: string;
        }>;
    };
    geography: {
        cities: Record<string, number>;
        operators: Record<string, number>;
    };
    users: Array<{
        id: string;
        full_name: string;
        email: string;
        phone: string;
        client_count: number;
        subscription_plan: string;
        subscription_status: string;
        active_until?: string | null;
        max_clients?: number | null;
        created_at: string;
        last_synced_at: string;
    }>;
    charts: {
        plans: Record<string, number>;
        growth: Array<{ date: string; count: number }>;
    };
    operations?: {
        feature_usage: {
            month: string;
            totals: Record<string, number>;
            top_users: Array<{
                user_id: string;
                full_name?: string | null;
                email?: string | null;
                measurement_pdf: number;
                receipt_document: number;
                total: number;
            }>;
        };
        webhook_events: Array<{
            event_key: string;
            event_type: string;
            transaction_id?: string | null;
            created_at: string;
        }>;
        app_logs: Array<{
            timestamp?: string | null;
            level?: string;
            event?: string;
            context?: Record<string, unknown> | null;
            raw: string;
        }>;
    };
    finance?: {
        totals: { count: number; amount: number };
        current_month: { amount: number; count: number };
        previous_month: { amount: number; count: number };
        new_subscribers: { current_month: number; previous_month: number };
        series: {
            daily: Array<{ label: string; amount: number; count: number }>;
            weekly: Array<{ label: string; amount: number; count: number }>;
            monthly: Array<{ label: string; amount: number; count: number }>;
            yearly: Array<{ label: string; amount: number; count: number }>;
        };
        recent_payments: Array<{
            transaction_id: string;
            user_id: string;
            plan: string;
            amount: number;
            currency: string;
            paid_at: string;
            full_name?: string | null;
            email?: string | null;
        }>;
    };
    user_details?: Record<string, unknown> | null;
    user_operations?: {
        month: string;
        usage: {
            measurement_pdf: number;
            receipt_document: number;
        };
        recent_logs: Array<{
            timestamp?: string | null;
            level?: string;
            event?: string;
            context?: Record<string, unknown> | null;
            raw: string;
        }>;
    } | null;
}

interface ErrorMonitoring {
    status: string;
    stats: {
        total_php_errors: number;
        total_sync_errors: number;
        critical_count: number;
        warning_count: number;
        info_count: number;
    };
    errors: Array<{
        timestamp: string;
        type: string;
        category: string;
        message: string;
        severity: 'critical' | 'warning' | 'info';
        recommendation: string;
        actionable_steps: string[];
    }>;
    system_info: {
        php_version: string;
        memory_limit: string;
        max_execution_time: string;
    };
}

interface AdminDashboardProps {
    onClose: () => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onClose }) => {
    const [secret, setSecret] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<AdminStats | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState<AdminStats['users'][0] | null>(null);
    const [selectedUserDetails, setSelectedUserDetails] = useState<Record<string, unknown> | null>(null);
    const [selectedUserOps, setSelectedUserOps] = useState<AdminStats['user_operations'] | null>(null);
    const [animate, setAnimate] = useState(false);
    const [activeTab, setActiveTab] = useState<'overview' | 'alerts' | 'geography' | 'logs' | 'operations' | 'finance'>('overview');
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [errorData, setErrorData] = useState<ErrorMonitoring | null>(null);
    const [errorFilter, setErrorFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/fedapay_integration/admin_api.php', {
                headers: { 'X-Admin-Secret': secret },
            });
            const result = await response.json();

            if (response.ok && result.status === 'ok') {
                setData(result);
                setIsAuthenticated(true);
                setTimeout(() => setAnimate(true), 100);
            } else {
                setError(result.message || 'Accès refusé.');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur.');
        } finally {
            setLoading(false);
        }
    };

    const refreshAdminData = async () => {
        if (!secret) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/fedapay_integration/admin_api.php', {
                headers: { 'X-Admin-Secret': secret },
            });
            const result = await response.json();
            if (response.ok && result.status === 'ok') {
                setData(result);
                if (selectedUser && result.user_operations) {
                    setSelectedUserOps(result.user_operations);
                }
                if (selectedUser && result.user_details) {
                    setSelectedUserDetails(result.user_details);
                }
            } else {
                setError(result.message || 'Acces refuse.');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur.');
        } finally {
            setLoading(false);
        }
    };

    const loadUserDetails = async (userId: string) => {
        if (!secret) return;
        try {
            const response = await fetch(`/fedapay_integration/admin_api.php?userId=${encodeURIComponent(userId)}`, {
                headers: { 'X-Admin-Secret': secret },
            });
            const result = await response.json();
            if (response.ok && result.status === 'ok') {
                setSelectedUserDetails(result.user_details || null);
                setSelectedUserOps(result.user_operations || null);
            }
        } catch {
            setSelectedUserDetails(null);
            setSelectedUserOps(null);
        }
    };

    const loadErrorData = async () => {
        if (!secret) return;
        try {
            const response = await fetch('/fedapay_integration/error_monitor.php', {
                headers: { 'X-Admin-Secret': secret },
            });
            const result = await response.json();
            if (response.ok && result.status === 'ok') {
                setErrorData(result);
            }
        } catch (err) {
            console.error('Failed to load error data:', err);
        }
    };

    const filteredUsers = useMemo(() => {
        if (!data) return [];
        const term = searchTerm.toLowerCase();
        return data.users.filter(user =>
            (user.full_name || '').toLowerCase().includes(term) ||
            (user.email || '').toLowerCase().includes(term) ||
            (user.phone || '').includes(term)
        );
    }, [data, searchTerm]);

    const topUsers = useMemo(() => {
        if (!data) return [];
        return [...data.users].sort((a, b) => b.client_count - a.client_count).slice(0, 3);
    }, [data]);

    const bestMonth = useMemo(() => {
        const monthly = data?.finance?.series?.monthly || [];
        if (monthly.length === 0) {
            return null;
        }
        return monthly.reduce((best, row) => {
            if (!best || row.amount > best.amount) {
                return row;
            }
            return best;
        }, null as { label: string; amount: number; count: number } | null);
    }, [data]);

    // Smart XLSX Export with multiple sheets (dynamic import for iOS compatibility)
    const downloadXLSX = async () => {
        if (!data) return;

        // Dynamic import to avoid Safari iOS crash
        const XLSX = await import('xlsx');

        const wb = XLSX.utils.book_new();

        // Sheet 1: KPIs
        const kpisData = [
            ['INDICATEURS CLÉS - Mesurette Dashboard'],
            [],
            ['Métrique', 'Valeur'],
            ['Total Inscrits', data.kpis.total_users],
            ['Clients Gérés', data.kpis.total_clients_managed],
            ['Abonnements Actifs', data.kpis.active_subscriptions],
            ['MRR (FCFA)', data.kpis.mrr],
            ['Revenus Totaux (FCFA)', data.kpis.total_revenue],
            ['Taux de Conversion (%)', data.kpis.conversion_rate],
            ['ARPU (FCFA)', data.kpis.arpu],
            [],
            ['ENGAGEMENT'],
            ['Utilisateurs Actifs (7j)', data.engagement?.active_users || 0],
            ['Super Users (>10 clients)', data.engagement?.super_users || 0],
            ['Utilisateurs Inactifs', data.engagement?.inactive_count || 0],
        ];
        const wsKpis = XLSX.utils.aoa_to_sheet(kpisData);
        XLSX.utils.book_append_sheet(wb, wsKpis, 'Statistiques');

        // Sheet 2: Users
        const usersData = [
            ['ID', 'Nom', 'Email', 'Téléphone', 'Clients', 'Plan', 'Statut', 'Inscrit le', 'Dernière Sync'],
            ...data.users.map(u => [
                u.id, u.full_name, u.email, u.phone, u.client_count,
                u.subscription_plan, u.subscription_status,
                new Date(u.created_at).toLocaleDateString(),
                u.last_synced_at ? new Date(u.last_synced_at).toLocaleDateString() : 'Jamais'
            ])
        ];
        const wsUsers = XLSX.utils.aoa_to_sheet(usersData);
        XLSX.utils.book_append_sheet(wb, wsUsers, 'Utilisateurs');

        // Sheet 3: Alerts
        const alertsData = [
            ['ABONNEMENTS EXPIRANT BIENTÔT'],
            ['Nom', 'Email', 'Plan', 'Expire le'],
            ...(data.alerts?.expiring_soon || []).map(u => [
                u.full_name, u.email, u.subscription_plan, new Date(u.active_until).toLocaleDateString()
            ]),
            [],
            ['UTILISATEURS INACTIFS (30+ jours)'],
            ['Nom', 'Email', 'Téléphone', 'Inscrit le'],
            ...(data.engagement?.inactive_list || []).map(u => [
                u.full_name, u.email, u.phone, new Date(u.created_at).toLocaleDateString()
            ])
        ];
        const wsAlerts = XLSX.utils.aoa_to_sheet(alertsData);
        XLSX.utils.book_append_sheet(wb, wsAlerts, 'Alertes');

        // Sheet 4: Geography
        const geoData = [
            ['RÉPARTITION GÉOGRAPHIQUE'],
            [],
            ['Ville', 'Utilisateurs'],
            ...Object.entries(data.geography?.cities || {}).map(([city, count]) => [city, count]),
            [],
            ['Opérateur', 'Utilisateurs'],
            ...Object.entries(data.geography?.operators || {}).map(([op, count]) => [op, count]),
        ];
        const wsGeo = XLSX.utils.aoa_to_sheet(geoData);
        XLSX.utils.book_append_sheet(wb, wsGeo, 'Géographie');

        XLSX.writeFile(wb, `mesurette_rapport_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // Smart PDF Export with charts and formatting (dynamic import for iOS compatibility)
    const downloadPDF = async () => {
        if (!data) return;

        // Dynamic import to avoid Safari iOS crash
        const { default: jsPDF } = await import('jspdf');

        const doc = new jsPDF();
        let yPos = 20;

        // Header
        doc.setFontSize(22);
        doc.setTextColor(79, 70, 229); // Indigo
        doc.text('Mesurette - Rapport Admin', 20, yPos);
        yPos += 10;
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Genere le ${new Date().toLocaleString('fr-FR')}`, 20, yPos);
        yPos += 15;

        // KPIs Section
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text('Indicateurs Cles', 20, yPos);
        yPos += 8;

        doc.setFontSize(10);
        const kpis = [
            [`Total Inscrits: ${data.kpis.total_users}`, `Clients Geres: ${data.kpis.total_clients_managed}`],
            [`Abonnements Actifs: ${data.kpis.active_subscriptions}`, `MRR: ${data.kpis.mrr} FCFA`],
            [`Taux de Conversion: ${data.kpis.conversion_rate}%`, `ARPU: ${data.kpis.arpu} FCFA`],
            [`Utilisateurs Actifs: ${data.engagement?.active_users || 0}`, `Super Users: ${data.engagement?.super_users || 0}`],
        ];

        kpis.forEach(([left, right]) => {
            doc.text(left, 20, yPos);
            doc.text(right, 110, yPos);
            yPos += 6;
        });

        yPos += 10;

        // Top 3 Users
        doc.setFontSize(14);
        doc.text('Hall of Fame - Top 3', 20, yPos);
        yPos += 8;
        doc.setFontSize(10);

        const top3 = [...data.users].sort((a, b) => b.client_count - a.client_count).slice(0, 3);
        top3.forEach((user, i) => {
            doc.text(`${i + 1}. ${user.full_name} - ${user.client_count} clients`, 25, yPos);
            yPos += 6;
        });

        yPos += 10;

        // Alerts Section
        const expiringCount = data.alerts?.expiring_soon?.length || 0;
        const inactiveCount = data.engagement?.inactive_count || 0;
        if (expiringCount > 0 || inactiveCount > 0) {
            doc.setFontSize(14);
            doc.setTextColor(234, 88, 12); // Orange
            doc.text('Alertes', 20, yPos);
            yPos += 8;
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);

            if (expiringCount > 0) {
                doc.text(`- ${expiringCount} abonnement(s) expirant bientot`, 25, yPos);
                yPos += 6;
            }
            if (inactiveCount > 0) {
                doc.text(`- ${inactiveCount} utilisateur(s) inactif(s) a relancer`, 25, yPos);
                yPos += 6;
            }
            yPos += 10;
        }

        // Geography Section
        doc.setFontSize(14);
        doc.setTextColor(59, 130, 246); // Blue
        doc.text('Repartition Geographique', 20, yPos);
        yPos += 8;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);

        const cities = Object.entries(data.geography?.cities || {}).sort(([, a], [, b]) => (b as number) - (a as number)).slice(0, 5);
        cities.forEach(([city, count]) => {
            doc.text(`${city}: ${count} utilisateurs`, 25, yPos);
            yPos += 6;
        });

        yPos += 10;

        // Users Table (first page only)
        if (yPos + 40 < 280) {
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.text('Utilisateurs Recents', 20, yPos);
            yPos += 8;
            doc.setFontSize(8);

            const tableData = data.users.slice(0, 10);
            tableData.forEach(user => {
                if (yPos > 270) return; // Prevent overflow
                doc.text(`${user.full_name} | ${user.email} | ${user.client_count} clients | ${user.subscription_plan}`, 25, yPos);
                yPos += 5;
            });
        }

        // Footer
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text('Mesurette 2025 - Dashboard Administrateur', 20, 285);

        doc.save(`mesurette_rapport_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const downloadCSV = () => {
        if (!data) return;
        const headers = ['ID', 'Nom', 'Email', 'Téléphone', 'Clients', 'Plan', 'Statut', 'Inscrit le', 'Dernière Sync'];
        const rows = data.users.map(u => [
            u.id, u.full_name, u.email, u.phone, u.client_count,
            u.subscription_plan, u.subscription_status, u.created_at, u.last_synced_at
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `mesurette_users_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    if (!isAuthenticated) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-90 backdrop-blur-sm">
                <div className="w-full max-w-md p-8 bg-white/10 border border-white/20 rounded-2xl shadow-2xl backdrop-blur-md text-center transform transition-all duration-500 scale-100 opacity-100">
                    <div className="mb-6 flex justify-center">
                        <div className="p-3 bg-indigo-500/20 rounded-full">
                            <svg className="w-8 h-8 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Accès Administrateur</h2>
                    <p className="text-gray-400 mb-6 text-sm">Veuillez entrer votre clé secrète pour accéder au tableau de bord.</p>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <input
                            type="password"
                            value={secret}
                            onChange={(e) => setSecret(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 text-white placeholder-gray-500 outline-none transition-all"
                            placeholder="Code secret administrateur"
                            autoFocus
                        />
                        {error && <p className="text-red-400 text-sm animate-pulse">{error}</p>}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg transform transition hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Vérification...' : 'Accéder au Dashboard'}
                        </button>
                    </form>
                    <button onClick={onClose} className="mt-6 text-gray-500 hover:text-white text-sm transition-colors">
                        Retour à l'accueil
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 bg-[#0f172a] text-white overflow-auto">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-[#0f172a]/80 backdrop-blur-md border-b border-white/10 px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-500/20 rounded-lg">
                        <svg className="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
                        Manager Dashboard
                    </h1>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={refreshAdminData}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition"
                    >
                        Rafraichir
                    </button>
                    {/* Export Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            Exporter
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>

                        {showExportMenu && (
                            <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50">
                                <button
                                    onClick={() => { downloadCSV(); setShowExportMenu(false); }}
                                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 transition flex items-center gap-3"
                                >
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    <div>
                                        <p className="font-medium">CSV Simple</p>
                                        <p className="text-xs text-gray-400">Liste utilisateurs</p>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { downloadXLSX(); setShowExportMenu(false); }}
                                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 transition flex items-center gap-3 border-t border-white/5"
                                >
                                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    <div>
                                        <p className="font-medium">Excel (XLSX)</p>
                                        <p className="text-xs text-gray-400">Multi-feuilles avec KPIs</p>
                                    </div>
                                </button>
                                <button
                                    onClick={() => { downloadPDF(); setShowExportMenu(false); }}
                                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/10 transition flex items-center gap-3 border-t border-white/5"
                                >
                                    <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                    <div>
                                        <p className="font-medium">Rapport PDF</p>
                                        <p className="text-xs text-gray-400">Document formaté</p>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-white transition rounded-lg hover:bg-white/5">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
            </header>

            {/* Tabs */}
            <div className="border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex gap-6">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors ${activeTab === 'overview' ? 'border-indigo-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
                        >
                            Vue d'ensemble
                        </button>
                        <button
                            onClick={() => setActiveTab('alerts')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors relative ${activeTab === 'alerts' ? 'border-indigo-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
                        >
                            Alertes
                            {data && data.alerts && ((data.alerts.expiring_soon?.length || 0) + (data.alerts.recently_expired?.length || 0)) > 0 && (
                                <span className="absolute -top-1 -right-2 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                                    {(data.alerts.expiring_soon?.length || 0) + (data.alerts.recently_expired?.length || 0)}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('geography')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors ${activeTab === 'geography' ? 'border-indigo-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
                        >
                            Carte Géographique
                        </button>
                        <button
                            onClick={() => { setActiveTab('logs'); loadErrorData(); }}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors relative ${activeTab === 'logs' ? 'border-indigo-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
                        >
                            Logs & Erreurs
                            {errorData && errorData.stats.critical_count > 0 && (
                                <span className="absolute -top-1 -right-2 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                                    {errorData.stats.critical_count}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('operations')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors ${activeTab === 'operations' ? 'border-indigo-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
                        >
                            Operations
                        </button>
                        <button
                            onClick={() => setActiveTab('finance')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors ${activeTab === 'finance' ? 'border-indigo-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
                        >
                            Finances
                        </button>
                    </div>
                </div>
            </div>

            <main className="p-6 max-w-7xl mx-auto space-y-8">
                {activeTab === 'overview' && (
                    <>
                        {/* KPIs Row 1 - Core Metrics */}
                        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 transform ${animate ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                            <KpiCard
                                title="Total Inscrits"
                                value={data?.kpis.total_users || 0}
                                icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
                                color="from-blue-500 to-cyan-400"
                            />
                            <KpiCard
                                title="Clients Gérés"
                                value={data?.kpis.total_clients_managed || 0}
                                icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>}
                                color="from-purple-500 to-pink-400"
                            />
                            <KpiCard
                                title="Abonnements Actifs"
                                value={data?.kpis.active_subscriptions || 0}
                                icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>}
                                color="from-emerald-500 to-teal-400"
                            />
                        </div>

                        {/* KPIs Row 2 - Financial & Engagement */}
                        <div className={`grid grid-cols-1 md:grid-cols-4 gap-6 transition-all duration-700 delay-75 transform ${animate ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                            <KpiCard
                                title="MRR"
                                value={data?.kpis.mrr || 0}
                                suffix=" FCFA"
                                icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                                color="from-green-500 to-emerald-400"
                            />
                            <KpiCard
                                title="Conversion"
                                value={data?.kpis.conversion_rate || 0}
                                suffix="%"
                                icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
                                color="from-yellow-500 to-orange-400"
                            />
                            <KpiCard
                                title="Utilisateurs Actifs"
                                value={data?.engagement.active_users || 0}
                                icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>}
                                color="from-indigo-500 to-purple-400"
                            />
                            <KpiCard
                                title="Super Users"
                                value={data?.engagement.super_users || 0}
                                icon={<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>}
                                color="from-pink-500 to-rose-400"
                            />
                        </div>

                        {/* Top Users Hall of Fame */}
                        <div className={`transition-all duration-700 delay-100 transform ${animate ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-yellow-400">
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                                Hall of Fame - Top Utilisateurs
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {topUsers.map((user, index) => (
                                    <div
                                        key={user.id}
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setSelectedUserDetails(null);
                                            setSelectedUserOps(null);
                                            loadUserDetails(user.id);
                                        }}
                                        className="cursor-pointer bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-5 rounded-2xl hover:scale-105 transition-transform flex items-center gap-4 group"
                                    >
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl shadow-lg
                         ${index === 0 ? 'bg-yellow-400 text-yellow-900' :
                                                index === 1 ? 'bg-gray-300 text-gray-900' :
                                                    'bg-amber-600 text-amber-100'}`}>
                                            #{index + 1}
                                        </div>
                                        <div>
                                            <p className="font-bold text-lg group-hover:text-indigo-300 transition-colors">{user.full_name || 'Sans nom'}</p>
                                            <p className="text-sm text-gray-400">{user.client_count} clients</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Users Table */}
                        <div className={`bg-white/5 border border-white/10 rounded-2xl overflow-hidden transition-all duration-700 delay-200 transform ${animate ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                            <div className="p-5 border-b border-white/10 flex flex-col md:flex-row justify-between gap-4">
                                <h3 className="text-lg font-bold">Tous les utilisateurs</h3>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Rechercher..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 pr-4 py-2 bg-black/20 border border-white/10 rounded-lg focus:ring-1 focus:ring-indigo-500 outline-none text-sm w-full md:w-64 text-white placeholder-gray-500"
                                    />
                                    <svg className="w-4 h-4 text-gray-500 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm text-gray-300">
                                    <thead className="bg-white/5 text-gray-100 uppercase text-xs">
                                        <tr>
                                            <th className="px-6 py-4">Nom</th>
                                            <th className="px-6 py-4">Contact</th>
                                            <th className="px-6 py-4">Clients</th>
                                            <th className="px-6 py-4">Plan</th>
                                            <th className="px-6 py-4">Dernière Sync</th>
                                            <th className="px-6 py-4 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {filteredUsers.map(user => (
                                            <tr key={user.id} className="hover:bg-white/5 transition-colors group">
                                                <td className="px-6 py-4 font-medium text-white">{user.full_name}</td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col">
                                                        <span>{user.email || 'N/A'}</span>
                                                        <span className="text-xs text-gray-500">{user.phone}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                                                        {user.client_count}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize border
                                                        ${user.subscription_plan === 'premium' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                                                            user.subscription_plan === 'pro' ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' :
                                                                user.subscription_plan === 'free' ? 'bg-slate-500/20 text-slate-400 border-slate-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'
                                                        }`}>
                                                        {user.subscription_plan}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-xs text-gray-500">
                                                    {user.last_synced_at ? new Date(user.last_synced_at).toLocaleDateString() : 'Jamais'}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setSelectedUserDetails(null);
                                                            setSelectedUserOps(null);
                                                            loadUserDetails(user.id);
                                                        }}
                                                        className="text-indigo-400 hover:text-indigo-300 font-medium"
                                                    >
                                                        Détails
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredUsers.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                                    Aucun utilisateur trouvé pour "{searchTerm}"
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'alerts' && (
                    <div className="space-y-6">
                        {/* Expiring Soon */}
                        {data && data.alerts && data.alerts.expiring_soon && data.alerts.expiring_soon.length > 0 && (
                            <div className="bg-orange-500/10 border border-orange-500/30 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-orange-400 mb-4 flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                    Abonnements Expirant Bientôt ({data.alerts.expiring_soon.length})
                                </h3>
                                <div className="space-y-3">
                                    {data.alerts.expiring_soon.map(user => (
                                        <div key={user.id} className="flex items-center justify-between bg-white/5 p-4 rounded-xl">
                                            <div>
                                                <p className="font-medium text-white">{user.full_name}</p>
                                                <p className="text-sm text-gray-400">{user.email} · Plan {user.subscription_plan}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-orange-400 font-medium">
                                                    Expire le {new Date(user.active_until).toLocaleDateString()}
                                                </p>
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(user.email)}
                                                    className="text-xs mt-1 bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-white transition"
                                                >
                                                    Copier email
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Inactive Users */}
                        {data && data.engagement && data.engagement.inactive_list && data.engagement.inactive_list.length > 0 && (
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-400 mb-4 flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                                    Utilisateurs Inactifs ({data.engagement.inactive_count || 0})
                                </h3>
                                <p className="text-sm text-gray-400 mb-4">Aucune synchronisation depuis 30 jours. Relancez-les !</p>
                                <div className="space-y-3">
                                    {data.engagement.inactive_list.slice(0, 10).map(user => (
                                        <div key={user.id} className="flex items-center justify-between bg-white/5 p-4 rounded-xl">
                                            <div>
                                                <p className="font-medium text-white">{user.full_name}</p>
                                                <p className="text-sm text-gray-400">{user.email}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-gray-500">
                                                    Inscrit le {new Date(user.created_at).toLocaleDateString()}
                                                </p>
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(user.email)}
                                                    className="text-xs mt-1 bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-white transition"
                                                >
                                                    Copier email
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {data && (!data.alerts?.expiring_soon?.length) && (!data.engagement?.inactive_count) && (
                            <div className="text-center py-12">
                                <svg className="w-16 h-16 mx-auto text-green-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <p className="text-xl font-bold text-white mb-2">Tout va bien !</p>
                                <p className="text-gray-400">Aucune alerte à signaler pour le moment.</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'geography' && data && data.geography && (
                    <div className="space-y-8">
                        {/* Header */}
                        <div className="text-center">
                            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">
                                Répartition Géographique
                            </h2>
                            <p className="text-gray-400">Distribution des utilisateurs par ville et opérateur</p>
                        </div>

                        {/* Cities Map */}
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                Villes Principales
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {Object.entries(data.geography.cities).sort(([, a], [, b]) => (b as number) - (a as number)).map(([city, count], index) => {
                                    const total = Object.values(data.geography.cities).reduce((sum, val) => (sum as number) + (val as number), 0) as number;
                                    const percentage = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                                    const colors = ['from-blue-500 to-cyan-400', 'from-purple-500 to-pink-400', 'from-green-500 to-emerald-400', 'from-yellow-500 to-orange-400', 'from-red-500 to-rose-400'];

                                    return (
                                        <div key={city} className="bg-white/5 p-5 rounded-xl hover:bg-white/10 transition-colors">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${colors[index % colors.length]} flex items-center justify-center text-white font-bold shadow-lg`}>
                                                        {index + 1}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-white">{city}</p>
                                                        <p className="text-xs text-gray-400">{count} utilisateur{(count as number) > 1 ? 's' : ''}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-bold text-white">{percentage}%</p>
                                                </div>
                                            </div>
                                            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                                                <div
                                                    className={`h-full bg-gradient-to-r ${colors[index % colors.length]} transition-all duration-1000 ease-out`}
                                                    style={{ width: `${percentage}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Operators Distribution */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {Object.entries(data.geography.operators).map(([operator, count]) => {
                                const total = Object.values(data.geography.operators).reduce((sum, val) => (sum as number) + (val as number), 0) as number;
                                const percentage = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                                const operatorColors: Record<string, string> = {
                                    'MTN': 'from-yellow-500 to-orange-400',
                                    'Moov': 'from-blue-500 to-cyan-400',
                                    'Autres': 'from-gray-500 to-slate-400'
                                };

                                return (
                                    <div key={operator} className="relative overflow-hidden bg-white/5 border border-white/10 p-6 rounded-2xl group hover:bg-white/10 transition-colors">
                                        <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity bg-gradient-to-br ${operatorColors[operator]} bg-clip-text text-transparent transform scale-150`}>
                                            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                        </div>
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className={`p-3 rounded-xl bg-gradient-to-br ${operatorColors[operator]} shadow-lg`}>
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                            </div>
                                            <p className="text-gray-400 font-medium text-sm uppercase tracking-wider">{operator}</p>
                                        </div>
                                        <p className="text-3xl font-bold text-white pl-2 mb-1">{count}</p>
                                        <p className="text-sm text-gray-400 pl-2">{percentage}% des utilisateurs</p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Benin Map Visualization */}
                        <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-white/10 rounded-2xl p-8">
                            <h3 className="text-xl font-bold mb-6 text-center">
                                🇧🇯 République du Bénin
                            </h3>
                            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                                <div className="text-center space-y-4">
                                    <div className="inline-block p-6 bg-white/10 rounded-2xl">
                                        <svg className="w-32 h-32 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-4xl font-bold text-white">{data.kpis.total_users}</p>
                                        <p className="text-gray-400">Utilisateurs Actifs</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 flex-1">
                                    {Object.entries(data.geography.cities).sort(([, a], [, b]) => (b as number) - (a as number)).slice(0, 4).map(([city, count]) => (
                                        <div key={city} className="bg-white/5 p-4 rounded-xl text-center">
                                            <p className="text-2xl font-bold text-white mb-1">{count}</p>
                                            <p className="text-xs text-gray-400 uppercase tracking-wider">{city}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'logs' && (
                    <div className="space-y-8">
                        {/* Header */}
                        <div className="text-center">
                            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-orange-300 mb-2">
                                🔍 Logs & Erreurs
                            </h2>
                            <p className="text-gray-400">Monitoring et diagnostics de l'application</p>
                        </div>

                        {errorData ? (
                            <>
                                {/* Stats Overview */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                    <div className="bg-red-500/10 border border-red-500/30 p-6 rounded-2xl">
                                        <p className="text-sm text-gray-400 mb-2">Erreurs Critiques</p>
                                        <p className="text-4xl font-bold text-red-400">{errorData.stats.critical_count}</p>
                                    </div>
                                    <div className="bg-yellow-500/10 border border-yellow-500/30 p-6 rounded-2xl">
                                        <p className="text-sm text-gray-400 mb-2">Avertissements</p>
                                        <p className="text-4xl font-bold text-yellow-400">{errorData.stats.warning_count}</p>
                                    </div>
                                    <div className="bg-blue-500/10 border border-blue-500/30 p-6 rounded-2xl">
                                        <p className="text-sm text-gray-400 mb-2">Informations</p>
                                        <p className="text-4xl font-bold text-blue-400">{errorData.stats.info_count}</p>
                                    </div>
                                    <div className="bg-green-500/10 border border-green-500/30 p-6 rounded-2xl">
                                        <p className="text-sm text-gray-400 mb-2">Système</p>
                                        <p className="text-lg font-bold text-green-400">PHP {errorData.system_info.php_version}</p>
                                        <p className="text-xs text-gray-500 mt-1">{errorData.system_info.memory_limit} RAM</p>
                                    </div>
                                </div>

                                {/* Filters */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setErrorFilter('all')}
                                        className={`px-4 py-2 rounded-lg transition ${errorFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                                    >
                                        Tout ({errorData.errors.length})
                                    </button>
                                    <button
                                        onClick={() => setErrorFilter('critical')}
                                        className={`px-4 py-2 rounded-lg transition ${errorFilter === 'critical' ? 'bg-red-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                                    >
                                        Critiques ({errorData.stats.critical_count})
                                    </button>
                                    <button
                                        onClick={() => setErrorFilter('warning')}
                                        className={`px-4 py-2 rounded-lg transition ${errorFilter === 'warning' ? 'bg-yellow-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                                    >
                                        Warnings ({errorData.stats.warning_count})
                                    </button>
                                    <button
                                        onClick={() => setErrorFilter('info')}
                                        className={`px-4 py-2 rounded-lg transition ${errorFilter === 'info' ? 'bg-blue-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                                    >
                                        Infos ({errorData.stats.info_count})
                                    </button>
                                </div>

                                {/* Error List */}
                                <div className="space-y-4">
                                    {errorData.errors
                                        .filter(err => errorFilter === 'all' || err.severity === errorFilter)
                                        .map((error, index) => {
                                            const severityColors = {
                                                critical: 'border-red-500/30 bg-red-500/5',
                                                warning: 'border-yellow-500/30 bg-yellow-500/5',
                                                info: 'border-blue-500/30 bg-blue-500/5'
                                            };
                                            const severityIcons = {
                                                critical: '🚨',
                                                warning: '⚠️',
                                                info: 'ℹ️'
                                            };

                                            return (
                                                <div key={index} className={`border ${severityColors[error.severity]} p-6 rounded-xl`}>
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-2xl">{severityIcons[error.severity]}</span>
                                                            <div>
                                                                <h3 className="font-bold text-white">{error.type}</h3>
                                                                <p className="text-xs text-gray-500">{error.timestamp}</p>
                                                            </div>
                                                        </div>
                                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${error.severity === 'critical' ? 'bg-red-500 text-white' :
                                                            error.severity === 'warning' ? 'bg-yellow-500 text-black' :
                                                                'bg-blue-500 text-white'
                                                            }`}>
                                                            {error.category}
                                                        </span>
                                                    </div>

                                                    <div className="bg-black/20 p-4 rounded-lg mb-4">
                                                        <p className="text-sm font-mono text-gray-300">{error.message}</p>
                                                    </div>

                                                    <div className="border-t border-white/10 pt-4">
                                                        <p className="text-sm font-bold text-indigo-400 mb-2">💡 Recommandation</p>
                                                        <p className="text-sm text-gray-300 mb-3">{error.recommendation}</p>

                                                        <p className="text-sm font-bold text-green-400 mb-2">✅ Actions à réaliser</p>
                                                        <ul className="space-y-1">
                                                            {error.actionable_steps.map((step, i) => (
                                                                <li key={i} className="text-sm text-gray-300 pl-4">• {step}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                    {errorData.errors.filter(err => errorFilter === 'all' || err.severity === errorFilter).length === 0 && (
                                        <div className="text-center py-12">
                                            <svg className="w-16 h-16 mx-auto text-green-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                            <p className="text-xl font-bold text-white mb-2">Aucune erreur dans cette catégorie</p>
                                            <p className="text-gray-400">L'application fonctionne normalement</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <div className="inline-block p-6 bg-white/10 rounded-full mb-4">
                                    <svg className="w-16 h-16 text-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                </div>
                                <p className="text-xl font-bold text-white mb-2">Chargement des logs...</p>
                                <p className="text-gray-400">Analyse en cours</p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'operations' && (
                    <div className="space-y-8">
                        <div className="text-center">
                            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-cyan-300 mb-2">
                                Operations & Quotas
                            </h2>
                            <p className="text-gray-400">Suivi des quotas, webhooks et logs applicatifs.</p>
                        </div>

                        {!data?.operations ? (
                            <div className="text-center py-12">
                                <p className="text-gray-400">Aucune donnee operations disponible.</p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <p className="text-sm text-gray-400 mb-2">PDF mesures (mois courant)</p>
                                        <p className="text-3xl font-bold text-white">
                                            {data.operations.feature_usage?.totals?.measurement_pdf ?? 0}
                                        </p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <p className="text-sm text-gray-400 mb-2">Recus/Bons (mois courant)</p>
                                        <p className="text-3xl font-bold text-white">
                                            {data.operations.feature_usage?.totals?.receipt_document ?? 0}
                                        </p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <p className="text-sm text-gray-400 mb-2">Webhooks traites</p>
                                        <p className="text-3xl font-bold text-white">
                                            {data.operations.webhook_events?.length ?? 0}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-lg font-bold text-white mb-4">Top usages quotas</h3>
                                        <div className="space-y-3">
                                            {(data.operations.feature_usage?.top_users || []).map((user) => (
                                                <div key={user.user_id} className="flex items-center justify-between bg-white/5 p-3 rounded-xl">
                                                    <div>
                                                        <p className="text-sm font-semibold text-white">{user.full_name || user.email || user.user_id}</p>
                                                        <p className="text-xs text-gray-400">{user.email || 'Email inconnu'}</p>
                                                    </div>
                                                    <div className="text-right text-xs text-gray-300">
                                                        <p>PDF: {user.measurement_pdf}</p>
                                                        <p>Recus: {user.receipt_document}</p>
                                                        <p>Total: {user.total}</p>
                                                    </div>
                                                </div>
                                            ))}
                                            {(data.operations.feature_usage?.top_users || []).length === 0 && (
                                                <p className="text-sm text-gray-400">Aucun usage quota ce mois.</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-lg font-bold text-white mb-4">Derniers webhooks</h3>
                                        <div className="space-y-3">
                                            {(data.operations.webhook_events || []).map((event, idx) => (
                                                <div key={`${event.event_key}-${idx}`} className="bg-white/5 p-3 rounded-xl text-xs text-gray-300">
                                                    <p className="text-white font-semibold">{event.event_type}</p>
                                                    <p className="text-gray-400">{event.event_key}</p>
                                                    <p>{event.transaction_id || 'Transaction inconnue'}</p>
                                                    <p className="text-gray-500">{new Date(event.created_at).toLocaleString()}</p>
                                                </div>
                                            ))}
                                            {(data.operations.webhook_events || []).length === 0 && (
                                                <p className="text-sm text-gray-400">Aucun webhook recent.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-white mb-4">Logs applicatifs recents</h3>
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {(data.operations.app_logs || []).map((log, idx) => (
                                            <div key={`log-${idx}`} className="bg-black/30 rounded-lg p-3 text-xs text-gray-300 font-mono">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-gray-500">{log.timestamp || ''}</span>
                                                    <span className="text-indigo-300">{log.level || ''}</span>
                                                    <span className="text-emerald-300">{log.event || ''}</span>
                                                </div>
                                                <div className="text-gray-400 break-words">{log.raw}</div>
                                            </div>
                                        ))}
                                        {(data.operations.app_logs || []).length === 0 && (
                                            <p className="text-sm text-gray-400">Aucun log recent.</p>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'finance' && (
                    <div className="space-y-8">
                        <div className="text-center">
                            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-cyan-300 mb-2">
                                Finances & Revenus
                            </h2>
                            <p className="text-gray-400">Suivi des paiements et evolution des revenus.</p>
                        </div>

                        {!data?.finance ? (
                            <div className="text-center py-12">
                                <p className="text-gray-400">Aucune donnee finance disponible.</p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                    <KpiCard
                                        title="Total encaisse"
                                        value={data.finance.totals.amount || 0}
                                        suffix=" FCFA"
                                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                                        color="from-emerald-500 to-teal-400"
                                    />
                                    <KpiCard
                                        title="Paiements"
                                        value={data.finance.totals.count || 0}
                                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 11h18M3 15h18M3 19h6" /></svg>}
                                        color="from-blue-500 to-cyan-400"
                                    />
                                    <KpiCard
                                        title="Ce mois"
                                        value={data.finance.current_month.amount || 0}
                                        suffix=" FCFA"
                                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
                                        color="from-purple-500 to-pink-400"
                                    />
                                    <KpiCard
                                        title="Nouveaux abonnes"
                                        value={data.finance.new_subscribers.current_month || 0}
                                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9l-6 6-3-3" /></svg>}
                                        color="from-yellow-500 to-orange-400"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Meilleur mois (12m)</p>
                                        <p className="text-2xl font-bold text-white">{bestMonth ? `${bestMonth.amount.toLocaleString()} FCFA` : '—'}</p>
                                        <p className="text-sm text-gray-500">{bestMonth ? bestMonth.label : 'Aucune donnee'}</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Comparatif mois</p>
                                        <p className="text-lg text-white font-semibold">{data.finance.current_month.amount.toLocaleString()} FCFA</p>
                                        <p className="text-xs text-gray-500">Mois courant ({data.finance.current_month.count} paiements)</p>
                                        <p className="mt-2 text-lg text-white font-semibold">{data.finance.previous_month.amount.toLocaleString()} FCFA</p>
                                        <p className="text-xs text-gray-500">Mois precedent ({data.finance.previous_month.count} paiements)</p>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Nouveaux abonnes</p>
                                        <p className="text-lg text-white font-semibold">{data.finance.new_subscribers.current_month}</p>
                                        <p className="text-xs text-gray-500">Ce mois</p>
                                        <p className="mt-2 text-lg text-white font-semibold">{data.finance.new_subscribers.previous_month}</p>
                                        <p className="text-xs text-gray-500">Mois precedent</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-lg font-bold text-white mb-4">Evolution mensuelle (12 mois)</h3>
                                        <div className="space-y-3">
                                            {(data.finance.series.monthly || []).map((row) => {
                                                const max = Math.max(...(data.finance.series.monthly || []).map((r) => r.amount));
                                                const width = max > 0 ? Math.round((row.amount / max) * 100) : 0;
                                                return (
                                                    <div key={row.label} className="flex items-center gap-3">
                                                        <span className="text-xs text-gray-400 w-16">{row.label}</span>
                                                        <div className="flex-1 bg-white/10 rounded-full h-3 overflow-hidden">
                                                            <div className="h-3 bg-gradient-to-r from-emerald-400 to-cyan-400" style={{ width: `${width}%` }} />
                                                        </div>
                                                        <span className="text-xs text-gray-300 w-24 text-right">{row.amount.toLocaleString()} FCFA</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-lg font-bold text-white mb-4">Evolution quotidienne (14 jours)</h3>
                                        <div className="space-y-3">
                                            {(data.finance.series.daily || []).map((row) => {
                                                const max = Math.max(...(data.finance.series.daily || []).map((r) => r.amount));
                                                const width = max > 0 ? Math.round((row.amount / max) * 100) : 0;
                                                return (
                                                    <div key={row.label} className="flex items-center gap-3">
                                                        <span className="text-xs text-gray-400 w-20">{row.label}</span>
                                                        <div className="flex-1 bg-white/10 rounded-full h-3 overflow-hidden">
                                                            <div className="h-3 bg-gradient-to-r from-indigo-400 to-purple-400" style={{ width: `${width}%` }} />
                                                        </div>
                                                        <span className="text-xs text-gray-300 w-24 text-right">{row.amount.toLocaleString()} FCFA</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-lg font-bold text-white mb-4">Evolution hebdomadaire (12 semaines)</h3>
                                        <div className="space-y-3">
                                            {(data.finance.series.weekly || []).map((row) => {
                                                const max = Math.max(...(data.finance.series.weekly || []).map((r) => r.amount));
                                                const width = max > 0 ? Math.round((row.amount / max) * 100) : 0;
                                                return (
                                                    <div key={row.label} className="flex items-center gap-3">
                                                        <span className="text-xs text-gray-400 w-20">{row.label}</span>
                                                        <div className="flex-1 bg-white/10 rounded-full h-3 overflow-hidden">
                                                            <div className="h-3 bg-gradient-to-r from-amber-400 to-orange-400" style={{ width: `${width}%` }} />
                                                        </div>
                                                        <span className="text-xs text-gray-300 w-24 text-right">{row.amount.toLocaleString()} FCFA</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                        <h3 className="text-lg font-bold text-white mb-4">Evolution annuelle</h3>
                                        <div className="space-y-3">
                                            {(data.finance.series.yearly || []).map((row) => (
                                                <div key={row.label} className="flex items-center justify-between text-sm text-gray-300">
                                                    <span>{row.label}</span>
                                                    <span className="text-white font-semibold">{row.amount.toLocaleString()} FCFA</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-white mb-4">Derniers paiements</h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm text-gray-300">
                                            <thead className="text-xs uppercase text-gray-400 border-b border-white/10">
                                                <tr>
                                                    <th className="py-2 pr-4">Client</th>
                                                    <th className="py-2 pr-4">Plan</th>
                                                    <th className="py-2 pr-4">Montant</th>
                                                    <th className="py-2 pr-4">Date</th>
                                                    <th className="py-2 pr-4">Transaction</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-white/5">
                                                {(data.finance.recent_payments || []).map((pay) => (
                                                    <tr key={pay.transaction_id}>
                                                        <td className="py-2 pr-4">
                                                            <div className="text-white">{pay.full_name || pay.email || pay.user_id}</div>
                                                            <div className="text-xs text-gray-500">{pay.email || ''}</div>
                                                        </td>
                                                        <td className="py-2 pr-4 capitalize">{pay.plan}</td>
                                                        <td className="py-2 pr-4">{pay.amount.toLocaleString()} {pay.currency || 'XOF'}</td>
                                                        <td className="py-2 pr-4">{new Date(pay.paid_at).toLocaleString()}</td>
                                                        <td className="py-2 pr-4 text-xs text-gray-500">{pay.transaction_id}</td>
                                                    </tr>
                                                ))}
                                                {(data.finance.recent_payments || []).length === 0 && (
                                                    <tr>
                                                        <td colSpan={5} className="py-6 text-center text-gray-500">
                                                            Aucun paiement enregistre.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </main>

            {/* User Details Modal */}
            {selectedUser && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedUser(null)}></div>
                    <div className="relative w-full max-w-lg bg-[#1e293b] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animation-pop-in">
                        <div className="h-32 bg-gradient-to-r from-indigo-600 to-purple-600 relative">
                            <button onClick={() => setSelectedUser(null)} className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 rounded-full text-white transition">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                            <div className="absolute -bottom-10 left-8">
                                <div className="w-20 h-20 rounded-full bg-slate-800 border-4 border-[#1e293b] flex items-center justify-center text-3xl font-bold text-white shadow-xl">
                                    {selectedUser.full_name?.charAt(0) || 'U'}
                                </div>
                            </div>
                        </div>
                        <div className="pt-12 pb-8 px-8">
                            <h2 className="text-2xl font-bold text-white mb-1">{selectedUser.full_name}</h2>
                            <p className="text-indigo-400 mb-6 font-medium">{selectedUser.subscription_plan.toUpperCase()} Member</p>

                            <div className="space-y-4">
                                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                                    <div className="p-2 bg-blue-500/20 rounded-lg"><svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div>
                                    <div className="flex-1 overflow-hidden">
                                        <p className="text-xs text-gray-500">Email</p>
                                        <p className="text-sm text-gray-200 truncate">{selectedUser.email}</p>
                                    </div>
                                    <button onClick={() => navigator.clipboard.writeText(selectedUser.email)} className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded text-white transition">Copier</button>
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                                    <div className="p-2 bg-green-500/20 rounded-lg"><svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg></div>
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500">Téléphone</p>
                                        <p className="text-sm text-gray-200">{selectedUser.phone}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-6">
                                    <div className="p-4 bg-white/5 rounded-xl text-center">
                                        <p className="text-3xl font-bold text-white mb-1">{selectedUser.client_count}</p>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider">Clients</p>
                                    </div>
                                    <div className="p-4 bg-white/5 rounded-xl text-center">
                                        <p className="text-3xl font-bold text-white mb-1">
                                            {Math.floor((new Date().getTime() - new Date(selectedUser.created_at).getTime()) / (1000 * 3600 * 24))}j
                                        </p>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider">Ancienneté</p>
                                    </div>
                                </div>

                                <div className="mt-6 grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-white/5 rounded-xl text-center">
                                        <p className="text-lg font-bold text-white mb-1">
                                            {selectedUserOps?.usage?.measurement_pdf ?? 0}
                                        </p>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider">PDF ce mois</p>
                                    </div>
                                    <div className="p-4 bg-white/5 rounded-xl text-center">
                                        <p className="text-lg font-bold text-white mb-1">
                                            {selectedUserOps?.usage?.receipt_document ?? 0}
                                        </p>
                                        <p className="text-xs text-gray-500 uppercase tracking-wider">Recus ce mois</p>
                                    </div>
                                </div>

                                <div className="mt-6 pt-6 border-t border-white/10">
                                    <p className="text-xs text-gray-500 mb-2">Dernière activité</p>
                                    <p className="text-sm text-gray-300">Synchronisation effectuée le {new Date(selectedUser.last_synced_at).toLocaleString()}</p>
                                </div>

                                <div className="mt-6 pt-6 border-t border-white/10">
                                    <p className="text-xs text-gray-500 mb-2">Derniers logs utilisateur</p>
                                    <div className="max-h-40 overflow-y-auto space-y-2 text-xs font-mono text-gray-300">
                                        {(selectedUserOps?.recent_logs || []).map((log, idx) => (
                                            <div key={`user-log-${idx}`} className="bg-black/30 rounded-lg p-2">
                                                <div className="flex flex-wrap gap-2 text-gray-400">
                                                    <span>{log.timestamp || ''}</span>
                                                    <span>{log.level || ''}</span>
                                                    <span>{log.event || ''}</span>
                                                </div>
                                                <div className="text-gray-300 break-words">{log.raw}</div>
                                            </div>
                                        ))}
                                        {(selectedUserOps?.recent_logs || []).length === 0 && (
                                            <p className="text-gray-500">Aucun log recent pour cet utilisateur.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const KpiCard = ({ title, value, icon, color, suffix = '' }: { title: string, value: number, icon: React.ReactNode, color: string, suffix?: string }) => (
    <div className="relative overflow-hidden bg-white/5 border border-white/10 p-6 rounded-2xl group hover:bg-white/10 transition-colors">
        <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity bg-gradient-to-br ${color} bg-clip-text text-transparent transform scale-150`}>
            {icon}
        </div>
        <div className="flex items-center gap-4 mb-4">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${color} shadow-lg`}>
                <div className="text-white w-6 h-6">{icon}</div>
            </div>
            <p className="text-gray-400 font-medium text-sm uppercase tracking-wider">{title}</p>
        </div>
        <p className="text-3xl font-bold text-white pl-2">{value.toLocaleString()}{suffix}</p>
    </div>
);

export default AdminDashboard;
